import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";

const PRIORITY_DOT = {
  High: "bg-[#DC2626]",
  Medium: "bg-[#F59E0B]",
  Low: "bg-[#3B82F6]",
};
const TYPE_LABEL = { overdue: "Overdue", today: "Today", tomorrow: "Tomorrow" };

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  const popRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data || []);
    } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClickAway = (e) => {
      if (!popRef.current) return;
      if (!popRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, [open]);

  const overdueCount = items.filter((n) => n.type === "overdue").length;
  const todayCount = items.filter((n) => n.type === "today").length;
  const total = items.length;

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="notification-bell"
        className="relative p-2 border border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {total > 0 && (
          <span
            className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 ${overdueCount > 0 ? "bg-[#DC2626]" : "bg-[#F59E0B]"} text-white text-[10px] font-bold flex items-center justify-center rounded-full`}
            data-testid="notification-count"
          >
            {total}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[70vh] overflow-y-auto bg-white border border-[#0a0a0a] shadow-2xl z-50"
          data-testid="notification-panel"
        >
          <div className="p-3 border-b border-[#E5E7EB] bg-[#0a0a0a] text-white flex items-center justify-between">
            <div>
              <div className="font-black text-sm tracking-tight">Follow-Up Reminders</div>
              <div className="text-[10px] opacity-80">
                {overdueCount > 0 && <span className="text-[#FCA5A5]">{overdueCount} overdue · </span>}
                {todayCount > 0 && <span>{todayCount} due today · </span>}
                {total === 0 && "All caught up"}
              </div>
            </div>
            <button
              onClick={() => { setOpen(false); navigate("/leads"); }}
              className="text-[10px] uppercase tracking-widest font-bold hover:underline"
              data-testid="notification-open-all"
            >
              All →
            </button>
          </div>
          <div className="divide-y divide-[#F3F4F6]">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-[#9CA3AF]">No reminders right now.</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => { setOpen(false); navigate(`/leads/${n.lead_id}`); }}
                className="w-full text-left px-3 py-3 hover:bg-[#F9FAFB] flex items-start gap-3"
                data-testid={`notif-item-${n.id}`}
              >
                <span className={`mt-1.5 w-2 h-2 ${PRIORITY_DOT[n.priority] || "bg-[#9CA3AF]"} rounded-full flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] uppercase tracking-widest font-bold ${n.type === "overdue" ? "text-[#DC2626]" : n.type === "today" ? "text-[#F59E0B]" : "text-[#3B82F6]"}`}>{TYPE_LABEL[n.type]}</span>
                    <span className="text-[10px] text-[#9CA3AF]">· {n.due_time || "Anytime"}</span>
                  </div>
                  <div className="font-semibold text-sm text-[#0a0a0a] truncate">{n.title.replace(/^.*?: /, "")}</div>
                  <div className="text-xs text-[#4B5563] truncate">{n.body}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
